<template>
	<div
		class="env-app-card-container"
		:tabindex="tabindex"
		:state="state"
		:compact="compact"
		@click.stop="handleClick($event)"
	>
		<div v-if="!compact" class="env-app-card-header">
			<h6 class="fw-normal fc-dark">
				{{ title }}
			</h6>
			<Icon
				v-if="actionIcon"
				:name="actionIcon"
				type="filled"
				size="small"
				:effects="false"
				:state="state.split('-')[0]"
				@click="action($event)"
			/>
		</div>
		<div class="env-app-card-body">
			<slot />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";

export default defineComponent({
	name: "AppCard",
	components: {
		Icon
	},
	props: {
		/**
		 * Title of Environment
		 */
		title: {
			type: String,
			required: true
		},
		/**
		 * Icon name to display as primary action (Optional)
		 */
		actionIcon: {
			type: String,
			default: null
		},
		/**
		 * 'default' | 'success' | 'success-light' | 'success-dark' | 'error' | 'warning'
		 */
		state: {
			type: String,
			default: "default",
			validator(value) {
				return [
					"default",
					"success",
					"success-light",
					"success-dark",
					"error",
					"error-light",
					"error-dark",
					"warning",
					"warning-light",
					"warning-dark",
					"primary",
					"primary-light",
					"primary-dark",
					"secondary",
					"secondary-light",
					"secondary-dark"
				].includes(value);
			}
		},
		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		},
		/**
		 * true | false
		 */
		compact: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		handleClick(e) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},
		action(e) {
			e.stopPropagation();
			/**
			 * Click on action icon
			 *
			 * @event action
			 */
			this.$emit("action", e);
		}
	}
});
</script>
<style lang="scss">
$states: (
	"default": var(--dashboard-border),
	"success": var(--dashboard-success),
	"success-light": var(--dashboard-success-light),
	"success-dark": var(--dashboard-success-dark),
	"error": var(--dashboard-danger),
	"error-light": var(--dashboard-danger-light),
	"error-dark": var(--dashboard-danger-dark),
	"warning": var(--dashboard-warning),
	"warning-light": var(--dashboard-warning-light),
	"warning-dark": var(--dashboard-warning-dark),
	"primary": var(--dashboard-primary),
	"primary-light": var(--dashboard-primary-light),
	"primary-dark": var(--dashboard-primary-dark),
	"secondary": var(--dashboard-secondary),
	"secondary-light": var(--dashboard-secondary-light),
	"secondary-dark": var(--dashboard-secondary-dark)
);

div.env-app-card-container {
	display: flex;
	flex-direction: column;
	position: relative;
	&:not([compact]) {
		min-height: 55px;
	}
	border-radius: 2px;

	&[compact] {
		height: 24px;
	}

	> div.env-app-card-header {
		padding: 8px;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	@each $state, $color in $states {
		&[state="#{$state}"] {
			background-color: $color;
		}
	}

	&:active,
	&:hover:not(:active) {
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: var(--dashboard-font-dark);
			border-radius: 2px;
		}
	}

	&:active {
		&:before {
			opacity: 0.08;
			cursor: pointer;
		}
	}

	&:hover:not(:active) {
		&:before {
			opacity: 0.04;
		}
	}

	&:focus {
		box-shadow: inset 0 0 0 2px var(--dashboard-primary);
		outline: none;
	}
}
</style>
